import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { logAuditAction } from "@/lib/audit";
import { formatCpfCnpj, formatPhone } from "@/lib/utils";
import { useState, useEffect } from "react";

export function useSettingsData(activeCompanyId: string | null) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    name: "",
    subtitle: "",
    slug: "",
    cnpj: "",
    phone: "",
    address: "",
    pix_key: "",
    receipt_footer: "",
    whatsapp_template: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

  const { data: company, isLoading } = useQuery({
    queryKey: ["company_settings", activeCompanyId],
    queryFn: async () => {
      if (!activeCompanyId) return null;
      const { data, error } = await supabase
        .from("company_settings")
        .select("*")
        .eq("id", activeCompanyId)
        .single();
      if (error) throw error;
      return data;
    },
  });

  const { data: subscription } = useQuery({
    queryKey: ["company_subscription", activeCompanyId],
    queryFn: async () => {
      if (!activeCompanyId) return null;
      const { data, error } = await supabase
        .from("company_subscriptions")
        .select("*, system_plans(name)")
        .eq("company_id", activeCompanyId)
        .single();
      if (error && error.code !== "PGRST116") throw error;
      return data;
    },
  });

  const { data: billingInfo, isLoading: loadingBilling } = useQuery({
    queryKey: ["billing_history", activeCompanyId],
    queryFn: async () => {
      return [
        {
          id: "1",
          date: "2024-03-01T10:00:00",
          amount: 149.9,
          status: "PAID",
          plan: "Pro",
        },
        {
          id: "2",
          date: "2024-02-01T10:00:00",
          amount: 149.9,
          status: "PAID",
          plan: "Pro",
        },
      ];
    },
  });

  useEffect(() => {
    if (company) {
      setFormData({
        name: company.name || "",
        subtitle: company.subtitle || "",
        slug: company.slug || "",
        cnpj: company.cnpj || "",
        phone: company.phone || "",
        address: company.address || "",
        pix_key: company.pix_key || "",
        receipt_footer: company.receipt_footer || "",
        whatsapp_template: company.whatsapp_template || "",
      });
    }
  }, [company]);

  const updateSettings = useMutation({
    mutationFn: async (updates: typeof formData) => {
      if (!activeCompanyId) throw new Error("Nenhuma empresa selecionada");

      const { error } = await supabase
        .from("company_settings")
        .update({
          name: updates.name,
          subtitle: updates.subtitle,
          slug: updates.slug,
          cnpj: updates.cnpj,
          phone: updates.phone,
          address: updates.address,
          pix_key: updates.pix_key,
          receipt_footer: updates.receipt_footer,
          whatsapp_template: updates.whatsapp_template,
        })
        .eq("id", activeCompanyId);

      if (error) {
        if (error.code === '23505' && error.message.includes('slug')) {
          throw new Error("Este link (slug) já está em uso por outro lava-jato. Escolha outro.");
        }
        throw error;
      }

      await logAuditAction({
        companyId: activeCompanyId,
        type: "SETTINGS_UPDATE",
        details: JSON.stringify({ old: company, new: updates }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["company_settings"] });
      setSuccessMsg("Configurações salvas com sucesso!");
      setErrorMsg("");
      setIsEditing(false);
      setTimeout(() => setSuccessMsg(""), 3000);
    },
    onError: (err: any) => {
      setErrorMsg(err.message || "Erro ao salvar.");
      setSuccessMsg("");
    },
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    
    let formattedValue = value;
    if (name === "cnpj") formattedValue = formatCpfCnpj(value);
    else if (name === "phone") formattedValue = formatPhone(value);
    else if (name === "slug") {
      // Allow only lowercase alphanumeric and hyphens for URL slug
      formattedValue = value.toLowerCase().replace(/[^a-z0-9\-]/g, "");
    }

    setFormData((prev) => ({ ...prev, [name]: formattedValue }));
  };

  const resetForm = () => {
    if (company) {
      setFormData({
        name: company.name || "",
        subtitle: company.subtitle || "",
        slug: company.slug || "",
        cnpj: company.cnpj || "",
        phone: company.phone || "",
        address: company.address || "",
        pix_key: company.pix_key || "",
        receipt_footer: company.receipt_footer || "",
        whatsapp_template: company.whatsapp_template || "",
      });
    }
    setIsEditing(false);
    setErrorMsg("");
    setSuccessMsg("");
  };

  return {
    company,
    subscription,
    billingInfo,
    loading: isLoading,
    loadingBilling,
    formData,
    setFormData,
    isEditing,
    setIsEditing,
    errorMsg,
    setErrorMsg,
    successMsg,
    isUpgradeModalOpen,
    setIsUpgradeModalOpen,
    updateSettings,
    handleChange,
    resetForm,
  };
}
